'use client';

/**
 * Adapted from shadcn-ui-expansions MultipleSelector component
 * @see https://github.com/hsuanyi-chou/shadcn-ui-expansions/blob/main/packages/cmdk/src/components/MultipleSelector.tsx
 */
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { faXmark } from '@icons/classic/regular';
import { clsxm } from '../utils';
import { Badge } from './Badge';
import { Command, CommandGroup, CommandItem, CommandList } from './Command';
import type { FormOption } from '../types';
interface GroupOption {
  [key: string]: FormOption[];
}
interface MultipleSelectorProps {
  value?: FormOption[];
  defaultOptions?: FormOption[];
  /** manually controlled options */
  options?: FormOption[];
  placeholder?: string;
  /** Loading component. */
  loadingIndicator?: React.ReactNode;
  /** Empty component. */
  emptyIndicator?: React.ReactNode;
  /** Debounce time for async search. Only work with `onSearch`. */
  delay?: number;
  /**
   * Only work with `onSearch` prop. Trigger search when `onFocus`.
   * For example, when user click on the input, it will trigger the search to get initial options.
   **/
  triggerSearchOnFocus?: boolean;
  /** async search */
  onSearch?: (value: string) => Promise<FormOption[]>;
  /**
   * sync search. This search will not showing loadingIndicator.
   * The rest props are the same as async search.
   * i.e.: creatable, groupBy, delay.
   **/
  onSearchSync?: (value: string) => FormOption[];
  onChange?: (options: FormOption[]) => void;
  /** Limit the maximum number of selected options. */
  maxSelected?: number;
  /** When the number of selected options exceeds the limit, the onMaxSelected will be called. */
  onMaxSelected?: (maxLimit: number) => void;
  /** Hide the placeholder when there are options selected. */
  hidePlaceholderWhenSelected?: boolean;
  disabled?: boolean;
  /** Group the options base on provided key. */
  groupBy?: string;
  className?: string;
  badgeClassName?: string;
  /**
   * First item selected is a default behavior by cmdk. That is why the default is true.
   * This is a workaround solution by add a dummy item.
   *
   * @reference: https://github.com/pacocoursey/cmdk/issues/171
   */
  selectFirstItem?: boolean;
  /** Allow user to create option when there is no option matched. */
  creatable?: boolean;
  /** Props of `Command` */
  commandProps?: React.ComponentPropsWithoutRef<typeof Command>;
  /** Props of `CommandInput` */
  inputProps?: Omit<React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>, 'value' | 'placeholder' | 'disabled'>;
  /** hide the clear all button. */
  hideClearAllButton?: boolean;
  /** Custom renderer for option labels in the dropdown (e.g., colored badges) */
  renderLabel?: (option: FormOption) => React.ReactNode;
  /**
   * Custom renderer for a selected chip. Receives the option and an `onRemove`
   * callback (call it to deselect). When provided, it fully replaces the default
   * badge — the renderer owns its own remove affordance.
   */
  renderBadge?: (option: FormOption, opts: {
    onRemove: () => void;
  }) => React.ReactNode;
  /**
   * Classes for the dropdown list. The dropdown defaults to the input's width
   * (`w-full`); override here to e.g. size it to content (`w-max min-w-full`).
   */
  dropdownClassName?: string;
}
export interface MultipleSelectorRef {
  selectedValue: FormOption[];
  input: HTMLInputElement;
  focus: () => void;
  reset: () => void;
}
export function useDebounce(value, delay) {
  const $ = _c(4);
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  let t0;
  let t1;
  if ($[0] !== delay || $[1] !== value) {
    t0 = () => {
      const timer = setTimeout(() => setDebouncedValue(value), delay || 500);
      return () => {
        clearTimeout(timer);
      };
    };
    t1 = [value, delay];
    $[0] = delay;
    $[1] = value;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return debouncedValue;
}
function transToGroupOption(options: FormOption[], groupBy?: string) {
  if (options.length === 0) {
    return {};
  }
  if (!groupBy) {
    return {
      '': options
    };
  }
  const groupOption: GroupOption = {};
  options.forEach(option => {
    // groupBy accesses an arbitrary property on the option object (e.g. 'category')
    const key = (option as unknown as Record<string, unknown>)[groupBy] as string || '';
    if (!groupOption[key]) {
      groupOption[key] = [];
    }
    groupOption[key].push(option);
  });
  return groupOption;
}
function removePickedOption(groupOption: GroupOption, picked: FormOption[]) {
  const cloneOption = JSON.parse(JSON.stringify(groupOption)) as GroupOption;
  for (const [key, value] of Object.entries(cloneOption)) {
    cloneOption[key] = value.filter(val => !picked.find(p => p.value === val.value));
  }
  return cloneOption;
}
function isOptionsExist(groupOption: GroupOption, targetOption: FormOption[]) {
  for (const [, value] of Object.entries(groupOption)) {
    if (value.some(option => targetOption.find(p => p.value === option.value))) {
      return true;
    }
  }
  return false;
}

/**
 * The `CommandEmpty` of shadcn/ui will cause the cmdk empty not rendering correctly.
 * So we create one and copy the `Empty` implementation from `cmdk`.
 *
 * @reference: https://github.com/hsuanyi-chou/shadcn-ui-expansions/issues/34#issuecomment-1949561607
 **/
const CommandEmpty = forwardRef<HTMLDivElement, React.ComponentProps<typeof CommandPrimitive.Empty>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const render = useCommandState(_temp);
  if (!render) {
    return null;
  }
  let t1;
  if ($[3] !== className) {
    t1 = clsxm("py-6 text-center text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <div className={t1} cmdk-empty="" ref={forwardedRef} role="presentation" {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
CommandEmpty.displayName = 'CommandEmpty';
const MultipleSelector = React.forwardRef<MultipleSelectorRef, MultipleSelectorProps>(({
  value,
  onChange,
  placeholder,
  defaultOptions: arrayDefaultOptions = [],
  options: arrayOptions,
  delay,
  onSearch,
  onSearchSync,
  loadingIndicator,
  emptyIndicator,
  maxSelected = Number.MAX_SAFE_INTEGER,
  onMaxSelected,
  hidePlaceholderWhenSelected,
  disabled,
  groupBy,
  className,
  badgeClassName,
  selectFirstItem = true,
  creatable = false,
  triggerSearchOnFocus = false,
  commandProps,
  inputProps,
  hideClearAllButton = false,
  renderLabel,
  renderBadge,
  dropdownClassName
}: MultipleSelectorProps, ref: React.Ref<MultipleSelectorRef>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [onScrollbar, setOnScrollbar] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null); // Added this

  const [selected, setSelected] = React.useState<FormOption[]>(value || []);
  const [options, setOptions] = React.useState<GroupOption>(transToGroupOption(arrayDefaultOptions, groupBy));
  const [inputValue, setInputValue] = React.useState('');
  const debouncedSearchTerm = useDebounce(inputValue, delay || 500);
  React.useImperativeHandle(ref, () => ({
    selectedValue: [...selected],
    input: inputRef.current as HTMLInputElement,
    focus: () => inputRef?.current?.focus(),
    reset: () => setSelected([])
  }), [selected]);
  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && inputRef.current && !inputRef.current.contains(event.target as Node)) {
      setOpen(false);
      inputRef.current.blur();
    }
  };
  const handleUnselect = React.useCallback((option: FormOption) => {
    const newOptions = selected.filter(s => s.value !== option.value);
    setSelected(newOptions);
    onChange?.(newOptions);
  }, [onChange, selected]);
  const handleCommandKeyDown = React.useCallback((e: React.KeyboardEvent<Element>) => {
    const {
      key
    } = e;
    const input = inputRef.current;
    if (input) {
      if (key === 'Delete' || key === 'Backspace') {
        if (input.value === '' && selected.length > 0) {
          const lastSelectOption = selected[selected.length - 1];
          // If there is a last item and it is not fixed, we can remove it.
          if (lastSelectOption && !lastSelectOption.fixed) {
            handleUnselect(lastSelectOption);
          }
        }
      }
      // This is not a default behavior of the <input /> field
      if (key === 'Escape') {
        input.blur();
      }
    }
  }, [handleUnselect, selected]);
  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [open]);
  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);
  useEffect(() => {
    /** If `onSearch` is provided, do not trigger options updated. */
    if (!arrayOptions || onSearch) {
      return;
    }
    const newOption = transToGroupOption(arrayOptions || [], groupBy);
    if (JSON.stringify(newOption) !== JSON.stringify(options)) {
      setOptions(newOption);
    }
  }, [arrayDefaultOptions, arrayOptions, groupBy, onSearch, options]);
  useEffect(() => {
    /** sync search */

    const doSearchSync = () => {
      const res = onSearchSync?.(debouncedSearchTerm);
      setOptions(transToGroupOption(res || [], groupBy));
    };
    const exec = async () => {
      if (!onSearchSync || !open) return;
      if (triggerSearchOnFocus) {
        doSearchSync();
      }
      if (debouncedSearchTerm) {
        doSearchSync();
      }
    };
    void exec();
  }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus]);
  useEffect(() => {
    /** async search */

    const doSearch = async () => {
      setIsLoading(true);
      const res_0 = await onSearch?.(debouncedSearchTerm);
      setOptions(transToGroupOption(res_0 || [], groupBy));
      setIsLoading(false);
    };
    const exec_0 = async () => {
      if (!onSearch || !open) return;
      if (triggerSearchOnFocus) {
        await doSearch();
      }
      if (debouncedSearchTerm) {
        await doSearch();
      }
    };
    void exec_0();
  }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus]);
  const CreatableItem = () => {
    if (!creatable) return undefined;
    if (isOptionsExist(options, [{
      value: inputValue,
      label: inputValue
    }]) || selected.find(s_0 => s_0.value === inputValue)) {
      return undefined;
    }
    const Item = <CommandItem className="cursor-pointer" onMouseDown={(e_0: React.MouseEvent) => {
      e_0.preventDefault();
      e_0.stopPropagation();
    }} onSelect={(value_0: string) => {
      if (selected.length >= maxSelected) {
        onMaxSelected?.(selected.length);
        return;
      }
      setInputValue('');
      const newOptions_0 = [...selected, {
        value: value_0,
        label: value_0
      }];
      setSelected(newOptions_0);
      onChange?.(newOptions_0);
    }} value={inputValue}>
          {`Create "${inputValue}"`}
        </CommandItem>;

    // For normal creatable
    if (!onSearch && inputValue.length > 0) {
      return Item;
    }

    // For async search creatable. avoid showing creatable item before loading at first.
    if (onSearch && debouncedSearchTerm.length > 0 && !isLoading) {
      return Item;
    }
    return undefined;
  };
  const EmptyItem = React.useCallback(() => {
    if (!emptyIndicator) return undefined;

    // For async search that showing emptyIndicator
    if (onSearch && !creatable && Object.keys(options).length === 0) {
      return <CommandItem disabled value="-">
            {emptyIndicator}
          </CommandItem>;
    }
    return <CommandEmpty>{emptyIndicator}</CommandEmpty>;
  }, [creatable, emptyIndicator, onSearch, options]);
  const selectables = React.useMemo<GroupOption>(() => removePickedOption(options, selected), [options, selected]);
  const showClearAllButton = !hideClearAllButton && !disabled && selected.length >= 1 && selected.filter(s_1 => s_1.fixed).length !== selected.length;

  // Check if there's anything to show in the dropdown
  const hasSelectableOptions = Object.values(selectables).some(group => group.length > 0);
  const hasCreatableContent = creatable && inputValue.length > 0;
  const hasEmptyContent = !!emptyIndicator && !hasSelectableOptions;
  const hasDropdownContent = isLoading || hasSelectableOptions || hasCreatableContent || hasEmptyContent;

  /** Avoid Creatable Selector freezing or lagging when paste a long string. */
  const commandFilter = React.useCallback(() => {
    if (commandProps?.filter) {
      return commandProps.filter;
    }
    if (creatable) {
      return (value_1: string, search: string) => {
        return value_1.toLowerCase().includes(search.toLowerCase()) ? 1 : -1;
      };
    }
    // Using default filter in `cmdk`. We don't have to provide it.
    return undefined;
  }, [creatable, commandProps?.filter]);
  return <Command ref={dropdownRef} {...commandProps} className={clsxm('h-auto overflow-visible bg-transparent', commandProps?.className)} filter={commandFilter()} label={commandProps?.label ?? inputProps?.['aria-label'] ?? placeholder ?? 'Options'} onKeyDown={(e_1: React.KeyboardEvent<Element>) => {
    handleCommandKeyDown(e_1);
    commandProps?.onKeyDown?.(e_1 as React.KeyboardEvent<HTMLDivElement>);
  }} shouldFilter={commandProps?.shouldFilter !== undefined ? commandProps.shouldFilter : !onSearch} // When onSearch is provided, we don't want to filter the options. You can still override it.
  >
        <div className={clsxm('min-h-10 rounded-md border border-input text-base focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset md:text-sm', {
      'px-3 py-2': selected.length !== 0,
      'cursor-text': !disabled && selected.length !== 0
    }, className)} onClick={() => {
      if (disabled) return;
      inputRef?.current?.focus();
    }}>
          <div className="relative flex flex-wrap gap-1">
            {selected.map(option_0 => {
          if (renderBadge) {
            return <React.Fragment key={option_0.value}>
                    {renderBadge(option_0, {
                onRemove: () => handleUnselect(option_0)
              })}
                  </React.Fragment>;
          }
          return <Badge className={clsxm('data-[disabled]:bg-muted-foreground data-[disabled]:text-muted data-[disabled]:hover:bg-muted-foreground', 'data-[fixed]:bg-muted-foreground data-[fixed]:text-muted data-[fixed]:hover:bg-muted-foreground', badgeClassName)} data-disabled={disabled || undefined} data-fixed={option_0.fixed} key={option_0.value}>
                  {option_0.label}
                  <button className={clsxm('ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2', (disabled || option_0.fixed) && 'hidden')} aria-label={`Remove ${option_0.label}`} onClick={() => handleUnselect(option_0)} onKeyDown={e_2 => {
              if (e_2.key === 'Enter') {
                handleUnselect(option_0);
              }
            }} onMouseDown={(e_3: React.MouseEvent) => {
              e_3.preventDefault();
              e_3.stopPropagation();
            }} type="button">
                    <FontAwesomeIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" icon={faXmark} />
                  </button>
                </Badge>;
        })}
            {/* Avoid having the "Search" Icon */}
            <CommandPrimitive.Input {...inputProps} className={clsxm('flex-1 bg-transparent outline-none placeholder:text-muted-foreground', {
          'w-full': hidePlaceholderWhenSelected,
          'px-3 py-2': selected.length === 0,
          'ml-1': selected.length !== 0,
          // Reserve space so the absolutely-positioned clear-all button
          // doesn't overlap trailing input text/placeholder.
          'pr-6': showClearAllButton
        }, inputProps?.className)} disabled={disabled} onBlur={event_0 => {
          if (!onScrollbar) {
            setOpen(false);
          }
          inputProps?.onBlur?.(event_0);
        }} onFocus={event_1 => {
          setOpen(true);
          inputProps?.onFocus?.(event_1);
        }} onValueChange={value_2 => {
          setInputValue(value_2);
          inputProps?.onValueChange?.(value_2);
        }} placeholder={hidePlaceholderWhenSelected && selected.length !== 0 ? '' : placeholder} ref={inputRef} value={inputValue} />
            <button className={clsxm('absolute ltr:right-0 rtl:left-0 h-6 w-6 p-0', !showClearAllButton && 'hidden')} aria-label="Clear all selected options" onClick={() => {
          setSelected(selected.filter(s_2 => s_2.fixed));
          onChange?.(selected.filter(s_3 => s_3.fixed));
        }} type="button">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
        <div className={clsxm('relative', className)}>
          <CommandList className={clsxm('absolute top-1 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in', (!open || !hasDropdownContent) && 'hidden', dropdownClassName)} hidden={!open || !hasDropdownContent} onMouseEnter={() => {
        setOnScrollbar(true);
      }} onMouseLeave={() => {
        setOnScrollbar(false);
      }} onMouseUp={() => {
        inputRef?.current?.focus();
      }}>
            {isLoading ? <>{loadingIndicator}</> : <>
                {EmptyItem()}
                {CreatableItem()}
                {!selectFirstItem && <CommandItem className="hidden" value="-" />}
                {Object.entries(selectables).map(([key_0, dropdowns]) => <CommandGroup className="h-full overflow-auto" heading={key_0} key={key_0}>
                    <>
                      {dropdowns.map(option_1 => {
                return <CommandItem className={clsxm('cursor-pointer', option_1.disable && 'cursor-default text-muted-foreground')} disabled={option_1.disable} key={option_1.value} onMouseDown={(e_4: React.MouseEvent) => {
                  e_4.preventDefault();
                  e_4.stopPropagation();
                }} onSelect={() => {
                  if (selected.length >= maxSelected) {
                    onMaxSelected?.(selected.length);
                    return;
                  }
                  setInputValue('');
                  const newOptions_1 = [...selected, option_1];
                  setSelected(newOptions_1);
                  onChange?.(newOptions_1);
                }} value={option_1.label}>
                            {renderLabel ? renderLabel(option_1) : option_1.label}
                          </CommandItem>;
              })}
                    </>
                  </CommandGroup>)}
              </>}
          </CommandList>
        </div>
      </Command>;
});
MultipleSelector.displayName = 'MultipleSelector';
export default MultipleSelector;
function _temp(state) {
  return state.filtered.count === 0;
}